import React, { useState } from 'react';

interface Props {
  startDate: string;
  endDate: string;
  onChange: (start: string, end: string) => void;
  earliestDate?: string; // ISO date of oldest available data point
}

function daysAgo(n: number): string {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return d.toISOString().slice(0, 10);
}

const PRESETS = [
  { label: '7 days', days: 7 },
  { label: '30 days', days: 30 },
  { label: '90 days', days: 90 },
];

export function DateRangePicker({ startDate, endDate, onChange, earliestDate }: Props) {
  const [validationError, setValidationError] = useState<string | null>(null);

  const today = new Date().toISOString().slice(0, 10);

  const handlePreset = (days: number) => {
    setValidationError(null);
    onChange(daysAgo(days), today);
  };

  // A preset is useful only if it would reveal data older than what the previous
  // (smaller) preset already shows. Hide it otherwise to avoid user confusion.
  const isPresetUseful = (presetDays: number, index: number): boolean => {
    if (!earliestDate) { return true; }
    const previousPresetDays = index === 0 ? 0 : PRESETS[index - 1].days;
    const previousCutoff = daysAgo(previousPresetDays);
    return earliestDate < previousCutoff;
  };

  const handleStartChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newStart = e.target.value;
    if (endDate && newStart > endDate) {
      setValidationError('Start date cannot be after end date.');
    } else {
      setValidationError(null);
      onChange(newStart, endDate);
    }
  };

  const handleEndChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newEnd = e.target.value;
    if (startDate && newEnd < startDate) {
      setValidationError('End date cannot be before start date.');
    } else {
      setValidationError(null);
      onChange(startDate, newEnd);
    }
  };

  const btnBase: React.CSSProperties = {
    padding: '0.35rem 0.8rem',
    border: '1px solid #d1d5db',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '0.85rem',
    fontWeight: 500,
  };

  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: '0.5rem' }}>
      {PRESETS.map((p, i) =>
        isPresetUseful(p.days, i) ? (
          <button
            key={p.days}
            onClick={() => handlePreset(p.days)}
            style={{
              ...btnBase,
              backgroundColor: startDate === daysAgo(p.days) && endDate === today ? '#1a2332' : '#fff',
              color: startDate === daysAgo(p.days) && endDate === today ? '#fff' : '#374151',
            }}
          >
            {p.label}
          </button>
        ) : null
      )}
      <input
        type="date"
        value={startDate}
        max={endDate || today}
        onChange={handleStartChange}
        aria-label="Start date"
        style={{ ...btnBase, backgroundColor: '#fff', color: '#374151' }}
      />
      <span style={{ color: '#6b7280', fontSize: '0.85rem' }}>to</span>
      <input
        type="date"
        value={endDate}
        min={startDate}
        max={today}
        onChange={handleEndChange}
        aria-label="End date"
        style={{ ...btnBase, backgroundColor: '#fff', color: '#374151' }}
      />
      {validationError && (
        <span
          data-testid="date-range-error"
          style={{ color: '#dc2626', fontSize: '0.82rem', width: '100%' }}
        >
          {validationError}
        </span>
      )}
    </div>
  );
}
